/**
 * Document Detail Page
 *
 * Shows full metadata for one document and offers Open/Download.
 * Routed from DocumentList row click at /documents/:id.
 */

import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, Download, ExternalLink, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/documents/StatusBadge';
import { VisibilityChips } from '@/components/documents/VisibilityChips';
import {
  getDocument,
  listDocumentVersions,
  getDocumentContentURL,
  type Document,
  type DocumentVersion,
} from '@/api/endpoints/documents';
import { useAuthenticatedQuery } from '@/hooks/useAuthenticatedQuery';
import { ApiError } from '@/api/client';
import type { Page } from '@/api/types';
import { getHumanReadableError } from '@/utils/errorMessages';
import { createLogger } from '@/utils/logger';
import { MAIN_APP_ROUTES } from '@/lib/routes';
import { useAuthStore } from '@/stores/auth';

const logger = createLogger('DocumentDetail');

export default function DocumentDetail() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  // useAuthenticatedQuery inherits the QueryClient's retry budget (retry:2,
  // no-retry-on-4xx): a cold-start 503 is retried by the api client and the
  // page stays in the skeleton state through it instead of flashing the
  // load error. refetch backs the user-initiated Retry action.
  const {
    data: doc,
    isLoading,
    isError,
    error,
    refetch,
    isRefetching,
  } = useAuthenticatedQuery<Document, ApiError>({
    queryKey: ['document', id],
    queryFn: () => getDocument(id as string),
    enabled: !!id,
  });

  // The version history. Its own query rather than a field on the document:
  // the two are separate reads server-side, and a history that fails to load
  // must not blank the document page around it. An empty page is a real
  // answer — see listDocumentVersions — so this renders "no history recorded"
  // rather than an error for it.
  const { data: versions, isError: versionsFailed } = useAuthenticatedQuery<
    Page<DocumentVersion>,
    ApiError
  >({
    queryKey: ['document-versions', id],
    queryFn: () => listDocumentVersions(id as string),
    enabled: !!id,
  });

  const errorMessage = getHumanReadableError(error?.code ?? '', t('documents.loadError'));

  // useAuthenticatedQuery does not forward an onError/meta hook, so surface the
  // load failure to structured logging here (the migration off the old manual
  // fetch dropped the logger.error otherwise).
  useEffect(() => {
    if (isError) {
      logger.error('Failed to load document', { documentId: id, error });
    }
  }, [isError, error, id]);

  const user = useAuthStore((s) => s.user);

  const isURL = doc?.source_type === 'url' && doc.source_id.startsWith('http');
  // `content` is the server's own pointer to the bytes — present exactly when
  // there are bytes to fetch, so its absence is the answer "nothing to
  // download" without this page re-deriving that from source_type + gcs_path.
  const uploadedContent = doc?.source_type === 'upload' ? doc.content : undefined;
  // The original bytes exist in GCS from the moment the upload returned 200,
  // so download must not be held hostage by a failed ENRICHMENT stage
  // (partial_error/error are terminal verdicts, not in-flight states).
  // Withheld only while still processing — or QUARANTINED (scanner-flagged
  // bytes are never offered for download, that's the point of the state).
  const processing = doc?.status === 'processing';
  const quarantined = doc?.status === 'quarantined';
  // Whether the viewer can act on a failure (Retry is owner/editor). The
  // detail endpoint carries the PRECISE ACL role (unlike the list), so
  // effective_role is authoritative here; owner_id covers the own-doc case.
  const canAct =
    doc?.effective_role === 'owner' ||
    doc?.effective_role === 'editor' ||
    (doc != null && doc.owner_id === user?.id);

  return (
    <div className="min-h-screen bg-neutral-50">
      <header className="sticky top-0 z-10 bg-white border-b border-neutral-200">
        <div className="px-4 h-14 flex items-center gap-3">
          <button
            onClick={() => navigate(MAIN_APP_ROUTES.documents)}
            aria-label={t('documents.detail.backToList', 'Back to documents')}
            className="w-9 h-9 -ml-2 flex items-center justify-center text-primary"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h1 className="text-base font-semibold text-neutral-900 truncate">
            {doc?.title || t('documents.detail.title', 'Document')}
          </h1>
        </div>
      </header>

      <main className="p-4 max-w-3xl mx-auto space-y-4">
        {isError ? (
          <Card className="p-4 bg-red-50 border-red-200">
            <div className="flex items-start justify-between gap-4">
              <p className="text-sm text-red-700">{errorMessage}</p>
              <Button
                onClick={() => void refetch()}
                disabled={isRefetching}
                variant="outline"
                size="sm"
                className="flex items-center gap-2 shrink-0"
              >
                <RefreshCw className={`w-4 h-4 ${isRefetching ? 'animate-spin' : ''}`} />
                {isRefetching ? t('actions.retrying', 'Retrying...') : t('actions.retry', 'Retry')}
              </Button>
            </div>
          </Card>
        ) : isLoading || !doc ? (
          <div className="space-y-3">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : (
          <>
            <Card className="p-4 space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <StatusBadge status={doc.status} canAct={canAct} />
                {doc.category && (
                  <span className="text-xs text-neutral-600 px-2 py-0.5 rounded-full bg-neutral-100">
                    {doc.category}
                  </span>
                )}
                {/* ACL-derived visibility — the same truth the Share dialog
                    manages. The dead `scope` column previously shown here
                    could contradict the row the user just came from. */}
                <VisibilityChips doc={doc} viewerId={user?.id} />
              </div>
              {/* Raw failure detail is for people who can act on it; for a
                  reader a partial_error document simply works (see
                  StatusBadge.canAct). Hard failures stay visible to all. */}
              {doc.error_message && (doc.status !== 'partial_error' || canAct) && (
                <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
                  {doc.error_message}
                </p>
              )}
              <div className="flex flex-wrap gap-2 pt-2">
                {isURL && (
                  <a href={doc.source_id} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline">
                      <ExternalLink className="w-4 h-4 mr-2" /> {t('documents.detail.openSource', 'Open source')}
                    </Button>
                  </a>
                )}
                {uploadedContent && !processing && !quarantined && (
                  <a href={getDocumentContentURL(uploadedContent, 'attachment')}>
                    <Button>
                      <Download className="w-4 h-4 mr-2" /> {t('documents.detail.download', 'Download')}
                    </Button>
                  </a>
                )}
                {processing && (
                  <span className="text-xs text-neutral-500 self-center">
                    {t('documents.detail.notReady', 'Bytes available once processing finishes.')}
                  </span>
                )}
              </div>
            </Card>

            <Card className="p-4">
              <h2 className="text-sm font-semibold text-neutral-700 mb-3">
                {t('documents.detail.metadata', 'Metadata')}
              </h2>
              <dl className="text-sm divide-y divide-neutral-100">
                <Row label="ID" value={doc.id} mono />
                {doc.owner_name && (
                  <Row label={t('documents.detail.owner', 'Owner')} value={doc.owner_name} />
                )}
                <Row label={t('documents.detail.filename', 'Filename')} value={doc.filename} />
                <Row label={t('documents.detail.mimeType', 'MIME')} value={doc.mime_type} />
                <Row label={t('documents.detail.sourceType', 'Source type')} value={doc.source_type} />
                {doc.source_id && (
                  <Row label={t('documents.detail.sourceId', 'Source')} value={doc.source_id} mono />
                )}
                {doc.gcs_path && <Row label="GCS path" value={doc.gcs_path} mono />}
                <Row label={t('documents.detail.chunkCount', 'Chunks')} value={String(doc.chunk_count)} />
                <Row label={t('documents.detail.createdAt', 'Created')} value={doc.created_at} />
                <Row label={t('documents.detail.updatedAt', 'Updated')} value={doc.updated_at} />
                {doc.scope_target && (
                  <Row label={t('documents.detail.scopeTarget', 'Scope target')} value={doc.scope_target} />
                )}
                {doc.metadata && Object.keys(doc.metadata).length > 0 &&
                  Object.entries(doc.metadata).map(([k, v]) => (
                    <Row key={k} label={k} value={String(v)} />
                  ))}
              </dl>
            </Card>

            <Card className="p-4">
              <h2 className="text-sm font-semibold text-neutral-700 mb-3">
                {t('documents.detail.versions', 'Versions')}
              </h2>
              {versionsFailed ? (
                <p className="text-sm text-neutral-500">
                  {t('documents.detail.versionsUnavailable', 'Version history could not be loaded.')}
                </p>
              ) : !versions || versions.items.length === 0 ? (
                <p className="text-sm text-neutral-500">
                  {t('documents.detail.versionsEmpty', 'No version history recorded for this document.')}
                </p>
              ) : (
                <dl className="text-sm divide-y divide-neutral-100">
                  {versions.items.map((v) => (
                    <Row
                      key={v.id}
                      label={t('documents.detail.versionNo', 'v{{no}}', { no: v.version_no })}
                      value={`${v.status} · ${v.created_at}`}
                    />
                  ))}
                </dl>
              )}
            </Card>
          </>
        )}
      </main>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  if (!value) return null;
  return (
    <div className="grid grid-cols-3 gap-2 py-2">
      <dt className="text-neutral-500">{label}</dt>
      <dd className={`col-span-2 text-neutral-900 break-all ${mono ? 'font-mono text-xs' : ''}`}>{value}</dd>
    </div>
  );
}
